/**
 * trendDeltas.ts — 2.1b (§16.2.4, ruled §22.5, wire §22.6): the render rules
 * for the k-metric delta line, from the /v1/sales/reports wire ONLY.
 *
 * The wire carries ONE captured prior day per member ({prior, asOf} | null)
 * beside the live figure the same payload already shows; the screen takes
 * the difference and names the day it is against ("vs 1 Aug" — a gap moves
 * asOf and the copy says so, never silently). A null member is ABSENT: the
 * card renders NO delta line, and the page renders ONE footnote for all of
 * them (historyFootnote) — R3 on the screen: the absence is explained, not
 * silent, and never a 0.
 *
 * RULED 2026-09-26 (Klemen): the server's reason sentence
 * (deltas.absent[member], composed in internal/modules/sales/deltas.go from
 * contracts.SalesDeltaAbsentNoPrior — "no snapshot captured on or before the
 * boundary 2026-08-01 — history since 2026-09-07") is a support/log sentence
 * and is NOT rendered on a client-facing page; P2-15's "— <reason>" line and
 * its orphan "History since 7 September" line are replaced by the calm-
 * minimal spec's footnote, "Pipeline history starts 7 September. Trends
 * appear once a full month is captured." The date is the wire's
 * historySince — the tenant's first captured day, read from the
 * sales_pipeline_snapshot_runs ledger — never typed. The reason stays on the
 * wire (a support reply can still quote it); nothing here reads it.
 *
 * Nothing here recomputes a prior from the deals list. The prior is the
 * server's captured figure or it is absent; the only arithmetic is the
 * subtraction §22.6 assigns to the screen.
 */
import type { SalesReportDelta, SalesReportDeltas } from '../../../../api/types'

export const ABSENT_DELTA = '—'
export type DeltaTone = 'up' | 'down' | 'flat' | 'absent'
export interface DeltaLine { text: string; tone: DeltaTone }

// Fixed tables, no locale: the ISO date is sliced, never parsed through Date,
// so a browser timezone can never move "2026-08-01" to 31 July.
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

function dayAndMonth(iso: string): { day: number; month: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return null
  const month = Number(m[2]); const day = Number(m[3])
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  return { day, month }
}

/** "2026-08-01" → "1 Aug" (the §22.5 "vs 1 Oct" form). An unparseable
 *  date echoes verbatim rather than inventing one. */
export function shortDay(iso: string): string {
  const p = dayAndMonth(iso)
  return p ? `${p.day} ${MONTH_SHORT[p.month - 1]}` : iso
}

/** "2026-09-07" → "7 September" (the P2-15 "History since" form). */
export function longDay(iso: string): string {
  const p = dayAndMonth(iso)
  return p ? `${p.day} ${MONTH_LONG[p.month - 1]}` : iso
}

/**
 * The delta line for one member.
 *   member undefined → null: the wire carries no such member (a pre-2.1b
 *     backend) — nothing to render and nothing to hide.
 *   member null → null: ABSENT (no captured day on or before the boundary).
 *     The card renders no line; the page's historyFootnote explains why for
 *     every absent member at once. The server's reason is not read.
 *   current not a number → "—": a prior exists but the card itself shows no
 *     live figure to compare against (avg deal size with no open deals).
 *   otherwise → "▲ £12k vs 1 Aug" / "▼ …" / "→ no change vs 1 Aug", the
 *     difference current − prior, formatted by the card's own formatter.
 */
export function deltaLine(
  member: SalesReportDelta | null | undefined,
  current: number | null | undefined,
  fmt: (n: number) => string,
): DeltaLine | null {
  if (member === undefined || member === null) return null
  if (current == null || !Number.isFinite(current)) return { text: ABSENT_DELTA, tone: 'absent' }
  const diff = current - member.prior
  const day = shortDay(member.asOf)
  if (diff === 0) return { text: `→ no change vs ${day}`, tone: 'flat' }
  return { text: `${diff > 0 ? '▲' : '▼'} ${fmt(Math.abs(diff))} vs ${day}`, tone: diff > 0 ? 'up' : 'down' }
}

/** The four trend members of the wire (SalesReportDeltas minus its two
 *  bookkeeping keys). A member is ABSENT when it is null. */
const DELTA_MEMBERS = ['pipelinePence', 'weightedPipelinePence', 'avgDealSizePence', 'openDealCount'] as const

/** How many of the wire's trend members are absent (null). 0 on a pre-2.1b
 *  wire (no deltas at all) — there is nothing pending on a wire that never
 *  promised a trend. */
export function absentMembers(deltas: SalesReportDeltas | undefined): number {
  if (!deltas) return 0
  return DELTA_MEMBERS.filter(k => deltas[k] === null).length
}

/**
 * The page's ONE footnote for the trend line — the calm-minimal spec's
 * sentence, with the date from the wire.
 *   historySince undefined (pre-2.1b wire) → null: nothing to render.
 *   historySince null (no capture yet) → the history has not started; trends
 *     are pending.
 *   a date + trendsPending → "Pipeline history starts 7 September. Trends
 *     appear once a full month is captured." — the §22.5 rule in the
 *     reader's words: the first honest month-over-month value needs a
 *     captured day on or before the previous month's first day.
 *   a date, nothing pending → "Pipeline history starts 7 September." — the
 *     second sentence would be false once the trends are on the cards.
 */
export function historyFootnote(historySince: string | null | undefined, trendsPending: boolean): string | null {
  if (historySince === undefined) return null
  if (historySince === null) return 'Pipeline history has not started yet. Trends appear once a full month is captured.'
  const starts = `Pipeline history starts ${longDay(historySince)}.`
  return trendsPending ? `${starts} Trends appear once a full month is captured.` : starts
}
